import React, { useState } from 'react';
import AppLayout from '../Layouts/AppLayout';
import { UpdateOwnPasswordData, updateOwnPassword, useAuth } from '@/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CircleUserRound, Mail, Calendar, Shield, Key } from 'lucide-react';
import { getPermissionDisplayName } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import UserProfileChangePasswordDialog from './UserProfileChangePasswordDialog';

const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  if (!user) {
    return (
      <AppLayout title="Mi Perfil">
        <div className="flex items-center justify-center min-h-64">
          <p className="text-gray-500">No se pudo cargar la información del usuario.</p>
        </div>
      </AppLayout>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleChangePassword = async (passwordData: UpdateOwnPasswordData) => {
    try {
      const response = await updateOwnPassword({ user: passwordData });
      toast.success(response.message || 'Contraseña actualizada correctamente');
    } catch (error: any) {
      const validationMessage = error?.response?.data?.errors?.current_password?.[0];
      const message = validationMessage || error?.response?.data?.message || 'Error al cambiar la contraseña';
      toast.error(message);
      throw error;
    }
  };

  return (
    <AppLayout title="Mi Perfil">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Información Principal */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CircleUserRound className="h-5 w-5" />
              Información Personal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="text-2xl">
                  {user.name?.charAt(0).toUpperCase() || <CircleUserRound className="h-8 w-8" />}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                    {user.name}
                  </h2>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>Usuario desde: {formatDate(user.created_at)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roles y Permisos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Roles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Roles Asignados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {user.roles && user.roles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.roles.map((role, index) => (
                    <Badge key={index} variant="secondary" className="capitalize">
                      {role.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No hay roles asignados</p>
              )}
            </CardContent>
          </Card>

          {/* Permisos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Permisos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {user.originalPermissions && user.originalPermissions.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {user.permissions.map((permission, index) => (
                    <div key={index} className="text-sm text-gray-600 py-1 px-2 bg-gray-50 rounded">
                      {getPermissionDisplayName(permission)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No hay permisos específicos asignados</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Seguridad</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Cambiá tu contraseña sin depender de un administrador.
              </p>
            </div>
            <Button onClick={() => setPasswordDialogOpen(true)}>
              Cambiar contraseña
            </Button>
          </CardHeader>
        </Card>

        {/* Información Adicional */}
        <Card>
          <CardHeader>
            <CardTitle>Información de Cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">ID de Usuario</label>
                <p className="text-gray-600 mt-1">{user.id}</p>
              </div>
              {user.updated_at && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Última Actualización</label>
                  <p className="text-gray-600 mt-1">{formatDate(user.updated_at)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <UserProfileChangePasswordDialog
        open={passwordDialogOpen}
        user={user}
        onClose={() => setPasswordDialogOpen(false)}
        onSave={handleChangePassword}
      />
    </AppLayout>
  );
};

export default UserProfile;
